import { CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { buildAnchorMap, type AnchorMap, type DocumentPosition } from "./anchor-map";
import { DocumentPane, type DocumentPaneApi } from "./document-pane";
import { HighlightRect, PdfDocumentProxy } from "./pdf-page";
import {
  BilingualLabel,
  SIGNATURE_KIND_LABELS,
  SIGNATURE_VERDICT_LABELS,
  SignatureV2,
  CompareSideV2,
  CompareSummaryV2,
  CompareResultV2,
  FindingV2,
  LocatorV2,
  SEVERITY_STYLES,
  TIER_STYLES
} from "./types";

/**
 * v2 karşılaştırma sonucunun görünümü.
 *
 * **Neden eski görünümden ayrı bir bileşen.** `document-compare.tsx` on dokuz sprint
 * boyunca büyümüş 4400 satırlık bir dosya ve altı ayrı karşılaştırma modunu taşıyor. v2'nin
 * sözleşmesi farklı (düz bulgu listesi, madde özeti, taşımalar, güven dökümü) ve onu o
 * dosyaya cıvatalamak iki sözleşmeyi tek koşul ağacında birleştirmek olurdu. Bu bileşen
 * kendi kendine yeterli; eski modlar cutover'a kadar dokunulmadan çalışıyor.
 *
 * **Sıralama ve filtreleme sunucunun kararı.** Bulgular önem ve güvene göre SIRALI geliyor
 * (tel sözleşmesinin parçası), bu yüzden burada yeniden sıralanmıyor — iki tüketicinin aynı
 * sırayı görmesi sözleşmenin sözü.
 */

interface CompareResultViewProps {
  result: CompareResultV2;
  /** Arayüz dili; `tr` dışındaki her değer İngilizce metin gösterir. */
  language?: string;
  /*
    `onNavigate` KALDIRILDI. Bulgu/madde listeleri kaldırıldıktan sonra gezinmeyi
    tetikleyecek bir arayüz yok; tetiklenemeyen bir geri çağrıyı sözleşmede tutmak, var
    olmayan bir yeteneği duyurmak olurdu. Bir gezinme arayüzü geri gelirse prop da geri gelir.
  */
  /**
   * İki tarafın ayrıştırılmış PDF dokümanları. Verildiğinde panel seçilen bulguyu KENDİ
   * sayfa görüntüleyicisinde iki tarafta yan yana gösteriyor.
   *
   * PDF'i panel indirmiyor: proje kuralı gereği ağ çağrıları servis katmanından geçiyor ve
   * ebeveyn zaten iki tarafı yüklüyor — ikinci bir indirme aynı baytları iki kez çekerdi.
   */
  firstDocument?: PdfDocumentProxy | null;
  secondDocument?: PdfDocumentProxy | null;
  /**
   * Sayfa görüntüsü yüklenirken oluşan hata. Ebeveyn bunu state'te tutuyordu ve HİÇBİR
   * YERDE çizmiyordu; gerçek sebep (geçersiz PDF, 415, ağ hatası) kullanıcıya hiç
   * ulaşmıyordu.
   */
  pageImageError?: string | null;
  /** Sayfa görüntüleri hâlâ yükleniyor mu — "yüklenmedi" ile "yükleniyor" ayrı şeyler. */
  pageImagesLoading?: boolean;
  /**
   * Krom teması. **Varsayılan `light`** — uygulamanın geri kalanıyla aynı.
   *
   * Bir önceki sürüm koyu kromu koda gömdü ve bu yanlıştı: bu bir Kuika bileşeni,
   * müşterinin temasının içinde yaşıyor. Koyu tema hâlâ mümkün ama İSTENEREK seçiliyor.
   */
  theme?: "light" | "dark";
  /**
   * Styling panelinden gelen satır içi stil. `color` / `backgroundColor` / `fontFamily` /
   * `fontSize` değerleri krom token'larına yönlendiriliyor, böylece panelde seçilen renk
   * yalnızca kök öğeyi değil BÜTÜN yüzeyleri (komut çubuğu, sayfa zemini, raf) etkiliyor.
   */
  style?: CSSProperties;
}

/**
 * Krom renkleri CSS DEĞİŞKENİ, sabit değil.
 *
 * <p><b>Kapatılan hata.</b> Bir önceki sürüm grafit bir kromu doğrudan koda gömdü. Bu bir
 * Kuika bileşeni: müşterinin uygulamasının içinde, onun temasıyla çalışıyor ve styling
 * panelinden verilen renk/font onu etkileyebilmeli. Tema dayatmak elementi platformun geri
 * kalanından koparır.</p>
 *
 * <p>Artık her krom rengi bir token. Varsayılan AÇIK — uygulamanın geri kalanıyla aynı.
 * Koyu tema hâlâ mümkün ama <b>opt-in</b> (`theme="dark"`), varsayılan değil.</p>
 *
 * <p><b>Anlam taşıyan renkler token DEĞİL:</b> önem/güven rozetlerinin kırmızısı veri,
 * dekor değil; tema değişse de anlamı değişmiyor.</p>
 */
export interface CompareRootStyle extends CSSProperties {
  "--kuika-cmp-bg"?: string;
  "--kuika-cmp-surface"?: string;
  "--kuika-cmp-panel"?: string;
  "--kuika-cmp-panel-text"?: string;
  "--kuika-cmp-border"?: string;
  "--kuika-cmp-text"?: string;
  "--kuika-cmp-dim"?: string;
  "--kuika-cmp-accent"?: string;
  "--kuika-cmp-accent-text"?: string;
  "--kuika-cmp-subtle"?: string;
  "--kuika-cmp-font-family"?: string;
  "--kuika-cmp-font-size"?: string;

  /*
    ANLAM RENKLERİ. Temaya duyarlı ama styling paneline AÇIK DEĞİL: bunlar veri taşıyor
    (eklendi / silindi / değişti, kesin / şüpheli / zayıf). Kullanıcının bu renkleri
    değiştirebilmesi, "kırmızı = kritik" sözleşmesini kırardı. `applyUserStyle` onlara
    dokunmuyor; tema değiştirir, panel değiştirmez.
  */
  "--kuika-cmp-added"?: string;
  "--kuika-cmp-added-bg"?: string;
  "--kuika-cmp-removed"?: string;
  "--kuika-cmp-removed-bg"?: string;
  "--kuika-cmp-changed"?: string;
  "--kuika-cmp-changed-bg"?: string;
  "--kuika-cmp-moved"?: string;
  "--kuika-cmp-moved-bg"?: string;
  "--kuika-cmp-warn"?: string;
  "--kuika-cmp-warn-bg"?: string;
}

/** Token adları tek yerde: bir yazım hatası rengi sessizce düşürürdü. */
const T = {
  bg: "var(--kuika-cmp-bg)",
  surface: "var(--kuika-cmp-surface)",
  panel: "var(--kuika-cmp-panel)",
  panelText: "var(--kuika-cmp-panel-text)",
  border: "var(--kuika-cmp-border)",
  text: "var(--kuika-cmp-text)",
  dim: "var(--kuika-cmp-dim)",
  accent: "var(--kuika-cmp-accent)",
  accentText: "var(--kuika-cmp-accent-text)",
  subtle: "var(--kuika-cmp-subtle)",
  added: "var(--kuika-cmp-added)",
  addedBg: "var(--kuika-cmp-added-bg)",
  removed: "var(--kuika-cmp-removed)",
  removedBg: "var(--kuika-cmp-removed-bg)",
  changed: "var(--kuika-cmp-changed)",
  changedBg: "var(--kuika-cmp-changed-bg)",
  moved: "var(--kuika-cmp-moved)",
  movedBg: "var(--kuika-cmp-moved-bg)",
  warn: "var(--kuika-cmp-warn)",
  warnBg: "var(--kuika-cmp-warn-bg)"
} as const;

/**
 * Temanın varsayılan değerleri.
 *
 * <p><b>Raf (liste) iki temada da AÇIK kalıyor</b> ve bu bilinçli: raf uzun metin okunan
 * yüzey, ve önem/güven renkleri (kırmızı/amber/yeşil) açık zemin için seçilmiş. Kromu
 * koyulaştırıp okuma yüzeyini açık bırakmak tutarlı bir belge görüntüleyici düzeni;
 * alternatifi koyu temada okunaksız kalan bir rozet paleti, yani yarı çalışan bir özellik
 * olurdu (değişmez I6).
 */
function themeTokens(dark: boolean): CompareRootStyle {
  if (dark) {
    return {
      "--kuika-cmp-bg": "#16191d",
      "--kuika-cmp-surface": "#1f242a",
      "--kuika-cmp-panel": "#ffffff",
      "--kuika-cmp-panel-text": "#111827",
      "--kuika-cmp-border": "#2b313a",
      "--kuika-cmp-text": "#e6eaf0",
      "--kuika-cmp-dim": "#98a2b3",
      "--kuika-cmp-accent": "#5aa2ff",
      "--kuika-cmp-accent-text": "#08111f",
      "--kuika-cmp-subtle": "#1f242a",
      "--kuika-cmp-added": "#4ade80",
      "--kuika-cmp-added-bg": "#14291c",
      "--kuika-cmp-removed": "#f87171",
      "--kuika-cmp-removed-bg": "#2c1a1c",
      "--kuika-cmp-changed": "#fbbf24",
      "--kuika-cmp-changed-bg": "#2c2418",
      "--kuika-cmp-moved": "#60a5fa",
      "--kuika-cmp-moved-bg": "#152436",
      "--kuika-cmp-warn": "#fcd34d",
      "--kuika-cmp-warn-bg": "#2a2418"
    };
  }
  return {
    "--kuika-cmp-bg": "#ffffff",
    "--kuika-cmp-surface": "#f3f4f6",
    "--kuika-cmp-panel": "#ffffff",
    "--kuika-cmp-panel-text": "#111827",
    "--kuika-cmp-border": "#e5e7eb",
    "--kuika-cmp-text": "#111827",
    "--kuika-cmp-dim": "#6b7280",
    "--kuika-cmp-accent": "#2563eb",
    "--kuika-cmp-accent-text": "#ffffff",
    "--kuika-cmp-subtle": "#f9fafb",
    "--kuika-cmp-added": "#15803d",
    "--kuika-cmp-added-bg": "#dcfce7",
    "--kuika-cmp-removed": "#b91c1c",
    "--kuika-cmp-removed-bg": "#fee2e2",
    "--kuika-cmp-changed": "#b45309",
    "--kuika-cmp-changed-bg": "#ffedd5",
    "--kuika-cmp-moved": "#1d4ed8",
    "--kuika-cmp-moved-bg": "#dbeafe",
    "--kuika-cmp-warn": "#b45309",
    "--kuika-cmp-warn-bg": "#fffbeb"
  };
}

/**
 * Styling panelinden gelen değerleri token'lara yönlendirir.
 *
 * <p>Kök öğeye satır içi bir renk vermek YETMİYOR: panelin iç yüzeyleri (komut çubuğu,
 * sayfa zemini, raf) kendi renklerini taşıyor ve kalıtımla değişmiyorlar. Tree-view
 * bileşeninde kurulmuş köprünün aynısı — panel değeri token'a yazılıyor, o token'ı okuyan
 * her yüzey birlikte değişiyor.</p>
 *
 * <p>React özel CSS özelliklerine `px` EKLEMİYOR; sayısal `fontSize` burada elle
 * normalleştiriliyor, aksi hâlde `font-size: 13` geçersiz olur ve sessizce düşerdi.</p>
 */
function applyUserStyle(tokens: CompareRootStyle, userStyle: CSSProperties): CompareRootStyle {
  const out: CompareRootStyle = { ...tokens };

  if (typeof userStyle.color === "string") {
    out["--kuika-cmp-text"] = userStyle.color;
    out["--kuika-cmp-panel-text"] = userStyle.color;
  }

  let background: string | undefined;
  if (typeof userStyle.backgroundColor === "string") background = userStyle.backgroundColor;
  else if (typeof userStyle.background === "string") background = userStyle.background;
  if (background) {
    out["--kuika-cmp-bg"] = background;
    out["--kuika-cmp-panel"] = background;
  }

  if (typeof userStyle.fontFamily === "string") out["--kuika-cmp-font-family"] = userStyle.fontFamily;
  if (typeof userStyle.fontSize === "number") out["--kuika-cmp-font-size"] = `${userStyle.fontSize}px`;
  else if (typeof userStyle.fontSize === "string") out["--kuika-cmp-font-size"] = userStyle.fontSize;

  return out;
}

/**
 * Ölçüm yazı tipi.
 *
 * <p>Web fontu YÜKLENMİYOR ve bu bilinçli: bu bileşen üretilen müşteri uygulamalarının
 * içinde, kimi zaman çevrimdışı ve sıkı CSP altında çalışıyor; dış bir font isteği en kötü
 * anda sessizce düşerdi. Karakter tipografik kararlardan geliyor — sayılar mono ve tabular
 * (rakamlar sütun hâlinde hizalanıyor, bir alette olması gerektiği gibi).</p>
 */
const MONO = '"SF Mono", "JetBrains Mono", "IBM Plex Mono", ui-monospace, "Cascadia Mono", Menlo, Consolas, monospace';

/** Rakamların kaydırma sırasında zıplamaması için sabit genişlik. */
const TABULAR: CSSProperties = { fontVariantNumeric: "tabular-nums" };

/**
 * Sayfa genişliği sınırları (CSS pixel).
 *
 * Tek yerde: zoom butonu ve sığdırma hesabı AYNI sınırları kullanmak zorunda. Ayrı
 * durdukları sürümde sığdırma üst sınırsızdı ve bir düzen geri beslemesi belgeyi sonsuza
 * kadar yakınlaştırabiliyordu.
 */
const MIN_PAGE_WIDTH = 240;
const MAX_PAGE_WIDTH = 1600;
/** Sığdırmadan zoom'a geçerken kullanılan taban genişlik. */
const DEFAULT_ZOOM_WIDTH = 620;

/**
 * ÖLÇÜ SİSTEMİ — tek yerde, çünkü dağınıklığın sebebi tam olarak dağılmış sayılardı.
 *
 * <p><b>Kapatılan tasarım hatası.</b> Komut çubuğu tek satırda dört ayrı yazı boyutu
 * (22 / 13 / 11 / 10), üç ayrı kontrol yüksekliği (~24 / ~20 / native checkbox) ve iki
 * ayrı köşe yarıçapı (4 / 999) taşıyordu; dar ekranda da <c>flexWrap</c> ile ikinci
 * satıra sarıyordu. Sonuç, tasarlanmış değil DERLENMİŞ görünen bir çubuk.</p>
 *
 * <p>Profesyonel bir aletin çubuğu şu üç şeyi yapar: tek satırda kalır, her kontrolü aynı
 * yükseklikte tutar, ve boşluğu iki ölçeğe indirir (grup içi / gruplar arası). Sayılar
 * burada sabit olduğu için bir daha kayamıyor.</p>
 */
const BAR_HEIGHT = 40;
/** Her kontrolün yüksekliği. İstisnası yok — istisna, düzensizliğin başladığı yerdir. */
const CONTROL_HEIGHT = 26;
const RADIUS = 5;
/** Grup İÇİ boşluk. */
const GAP_TIGHT = 6;
/** Gruplar ARASI boşluk. */
const GAP_LOOSE = 16;
/** Çubuktaki tek gövde boyutu. İkinci boyut yalnızca ana ölçüm sayısı. */
const FONT_UI = 12;
const FONT_METRIC = 19;

/** Tek satır, sarmıyor: sarma düzensizliğin en görünür hâliydi. */
const barStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: GAP_LOOSE,
  flexWrap: "nowrap",
  height: BAR_HEIGHT,
  minHeight: BAR_HEIGHT,
  padding: `0 ${GAP_LOOSE / 2}px`,
  background: "var(--kuika-cmp-bg)",
  borderBottom: "1px solid var(--kuika-cmp-border)",
  fontSize: FONT_UI,
  overflow: "hidden"
};

/** Aynı hizada duran bir kontrol grubu. */
const groupStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: GAP_TIGHT,
  flexShrink: 0
};

/** Gruplar arası dikey ayırıcı: sabit yükseklik, ortalanmış. `stretch` çubuk sarınca eğri duruyordu. */
function divider(): ReactNode {
  return <span style={{ width: 1, height: 20, background: "var(--kuika-cmp-border)", flexShrink: 0 }} />;
}

/**
 * Pasif bilgi: hizada duruyor ama kutu yok.
 *
 * Kenarlıklı olduğu sürümde komşu düğmelerden ayırt edilemiyordu ve kullanıcı tıklıyordu.
 * Etkileşimli görünen her şey etkileşimli olmak zorunda.
 */
function passiveInfo(): CSSProperties {
  return {
    height: CONTROL_HEIGHT,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: FONT_UI,
    lineHeight: 1,
    cursor: "help",
    whiteSpace: "nowrap",
    flexShrink: 0
  };
}

/** Her kontrolün paylaştığı taban: aynı yükseklik, aynı yarıçap, aynı kenarlık. */
function controlBase(): CSSProperties {
  return {
    height: CONTROL_HEIGHT,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "0 9px",
    fontSize: FONT_UI,
    lineHeight: 1,
    border: "1px solid var(--kuika-cmp-border)",
    borderRadius: RADIUS,
    background: "var(--kuika-cmp-subtle)",
    color: "var(--kuika-cmp-text)",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0
  };
}

export function CompareResultView(props: CompareResultViewProps): JSX.Element {
  const { result, language, firstDocument, secondDocument, pageImageError, pageImagesLoading } = props;
  const isTr: boolean = (language ?? "tr").toLowerCase().startsWith("tr");

  /** Sayfa görüntüleyicide gösterilen bulgu; null ise görüntüleyici kapalı. */
  /**
   * Sayfa genişliği (CSS pixel) — zoom bunu değiştiriyor.
   *
   * Eskiden 420'ye SABİTTİ ve yoğun bir A4 maddesi okunamıyordu; zoom/fit altyapısı
   * (`_scanGlobalTargetWidth`, önbelleklenen viewport'lar, iptal edilebilir render
   * task'ları) hesaplanıyor ve ATILIYORDU. Artık okuyacak bir tüketicisi var.
   */
  /**
   * Sayfa genişliği (CSS pixel). `null` = KAPSAYICIYA SIĞDIR (varsayılan).
   *
   * Sabit bir sayı "sığdır" sözünü tutamaz: iki panel yan yana durduğu için dar bir ekranda
   * sayfalar kesiliyordu. Ölçülen kapsayıcı genişliği tutuyor.
   */
  const [pageWidth, setPageWidth] = useState<number | null>(null);
  /** Panellerin kapsayıcısı — sığdırma genişliği buradan ölçülüyor. */
  const panesRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number>(0);

  /**
   * Panellerin imperatif tutamakları.
   *
   * <p><b>Neden state değil ref.</b> Eşzamanlı kaydırma state üzerinden dönerken her
   * kaydırma olayı bir render turu demekti ve takip "takıla takıla" gidiyordu (ölçüldü).
   * Takip artık doğrudan DOM'a, <c>requestAnimationFrame</c> ile kare başına bir kez
   * yazılıyor; React hiç uyanmıyor.</p>
   */
  const firstApi = useRef<DocumentPaneApi | null>(null);
  const secondApi = useRef<DocumentPaneApi | null>(null);

  /**
   * Hangi taraf ŞU AN sürüyor ve ne zamana kadar.
   *
   * <p>Sahiplik TEK bir yerde: kullanıcı hangi paneli kaydırıyorsa o sürücü olur ve
   * bırakana kadar (son olaydan 250 ms sonra) diğerinin bildirimleri yok sayılır. Takipçi
   * asla sürücü olamaz, dolayısıyla kovalama imkânsız. Önceki sürümde her panel hem sürücü
   * hem takipçiydi ve 180 ms'lik bir "kendi kaydırmamı yok say" penceresi dolduğunda roller
   * takas olup paneller birbirini kovalıyordu.</p>
   */
  const driver = useRef<{ side: "first" | "second"; until: number } | null>(null);

  /** Eşzamanlı kaydırma açık mı. Çapa yoksa zaten kapalı ve kapatılamaz. */
  const [syncEnabled, setSyncEnabled] = useState<boolean>(true);

  /**
   * Çapa haritası. Motorun mantıksal düğüm eşleştirmelerinden geliyor; boşsa senkron
   * KAPANIR ve kullanıcıya söylenir — sessizce yanlış hizalamak daha kötü.
   */
  const anchorMap: AnchorMap = useMemo(() => buildAnchorMap(result.anchors ?? []), [result.anchors]);
  const syncActive: boolean = syncEnabled && anchorMap.usable;
  const viewerAvailable: boolean = hasViewer(firstDocument, secondDocument);

  /**
   * Kapsayıcı genişliğini ölç ve değiştikçe izle.
   *
   * `ResizeObserver` şart: panel bir generated app'in içinde ve genişliği ekran boyutuna,
   * yan panellere, kullanıcının pencereyi sürüklemesine göre değişiyor.
   */
  useEffect((): (() => void) => {
    const element = panesRef.current;
    if (!element) return (): void => undefined;

    const measure = (): void =>
      setMeasuredWidth((prev: number): number => (prev === element.clientWidth ? prev : element.clientWidth));
    measure();

    if (typeof ResizeObserver === "undefined") return (): void => undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return (): void => observer.disconnect();
  }, [viewerAvailable]);

  /**
   * Etkin sayfa genişliği: kapsayıcı iki panele bölünüyor, ayırıcı ve iç boşluk düşülüyor.
   *
   * <p><b>Üst sınır ölçümden BAĞIMSIZ.</b> Sığdırma ölçülen genişlikten hesaplanıyor ve
   * sonucun yine o genişliği etkileyebildiği bir düzende geri besleme oluşur — bir sürüm
   * boyunca belge sonsuza kadar yakınlaştı. Kök öğe artık kapsayıcıya bağlı (bkz.
   * `rootStyle`), ama ikinci emniyet olarak sonuç zoom'un üst sınırıyla kırpılıyor.</p>
   */
  const effectivePageWidth: number =
    pageWidth ??
    (measuredWidth > 0
      ? Math.min(MAX_PAGE_WIDTH, Math.max(MIN_PAGE_WIDTH, Math.floor((measuredWidth - 42) / 2)))
      : DEFAULT_ZOOM_WIDTH);

  /**
   * Bir panelden gelen kaydırma bildirimini karşı panele uygular.
   *
   * Sürücü kilidi burada: yalnızca sahibi olan taraf yazdırabiliyor. Takipçinin kendi
   * programatik kaydırmasından doğan bildirim, sahibi olmadığı için sessizce düşüyor —
   * geri besleme döngüsü kaynağında kesiliyor.
   */
  const driveFrom = (side: "first" | "second", position: DocumentPosition): void => {
    const now: number = Date.now();
    const current = driver.current;
    if (current && current.side !== side && current.until > now) return;
    driver.current = { side, until: now + 250 };

    const target: DocumentPosition | null =
      side === "first" ? anchorMap.firstToSecond(position) : anchorMap.secondToFirst(position);
    if (!target) return;
    const follower = side === "first" ? secondApi.current : firstApi.current;
    follower?.scrollToPosition(target);
  };

  /*
    Token'lar KÖK öğede tanımlanıyor; her iç yüzey onları `var()` ile okuyor. Kalıtımla
    çözülemezdi: komut çubuğu, sayfa zemini ve raf kendi arka planlarını taşıyor.
  */
  const rootStyle: CompareRootStyle = {
    ...applyUserStyle(themeTokens(props.theme === "dark"), props.style ?? {}),
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    /*
      SONSUZ ZOOM DÖNGÜSÜNÜN KAPATILMASI.

      Kök öğe bir flex satırının içinde duruyor ve `flex-grow`/`min-width` verilmediğinde
      genişliği İÇERİĞİNE göre belirleniyor. Sayfa genişliği ise ölçülen kapsayıcı
      genişliğinden hesaplanıyor. İkisi birleşince monoton bir geri besleme oluşuyordu:

        sayfa genişler → içerik genişler → kök genişler → ölçüm büyür → sayfa genişler → …

      Kullanıcının gördüğü hâli belgenin durmadan yakınlaşmasıydı. `flex: 1` + `minWidth: 0`
      + `width: 100%` kökü kapsayıcıya BAĞLIYOR: artık genişlik içeriğin değil kapsayıcının
      kararı ve döngü kaynağında kesiliyor. `overflow: hidden` de ekli — bir iç yüzey
      taşarsa kökü büyütmesin.
    */
    flex: 1,
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
    background: T.bg,
    color: T.text,
    fontFamily: "var(--kuika-cmp-font-family, inherit)",
    fontSize: "var(--kuika-cmp-font-size, 13px)"
  };

  return (
    <div data-testid="compare-result-view" style={rootStyle}>
      {/*
        TEK KOMUT ÇUBUĞU.

        Önceden üç ayrı bar üst üste duruyordu: özet (yapışkan), "Referans taraf" kutusu ve
        görüntüleyici araç çubuğu. Üçü birlikte ~150 px yüksekliği krom olarak yiyordu.
        Hepsi tek bir satırda: solda ölçüm, ortada görüntüleme kontrolleri, sağda raf.
        Referans bilgisi kayboldu değil — ölçümün yanındaki rozette, üzerine gelince tam
        gerekçesiyle birlikte.
      */}
      {renderCommandBar(result, pageWidth, setPageWidth, anchorMap, syncEnabled, setSyncEnabled, isTr)}

      {renderDegradedBanner(result, isTr)}

      {/*
        Görüntüleyici YOKSA sebebi SÖYLENİYOR, sessizce listeye düşülmüyor.

        Bu sessizlik tam olarak planın kovaladığı sınıf: paneller çizilmediğinde ekran
        eskisiyle birebir aynı görünüyor ve kullanıcı "değişiklik uygulanmamış" sanıyor.
      */}
      {!viewerAvailable && (
        <div
          style={{
            padding: "10px 12px",
            borderBottom: `1px solid ${T.border}`,
            background: pageImageError ? T.removedBg : T.warnBg,
            color: pageImageError ? T.removed : T.warn,
            fontSize: 12
          }}
        >
          {/*
            Üç DURUM ayrı ayrı söyleniyor: yükleniyor, hata var (SEBEBİYLE), ya da hiç
            başlamadı. Eskiden üçü tek genel satıra düşüyordu ve gerçek hata state'te
            kalıyordu — kullanıcı "değişiklik uygulanmamış" sanıyordu.
          */}
          {pageImagesLoading
            ? isTr
              ? "Sayfa görüntüleri yükleniyor…"
              : "Loading page images…"
            : pageImageError
              ? pageImageError
              : isTr
                ? "Sayfa görüntüleri yüklenmedi — bulgular sayfa üzerinde işaretlenemiyor. " +
                  "Bulgu rafındaki liste tam, yalnızca belge görünümü yok."
                : "Page images are not loaded — findings cannot be marked on the document. " +
                  "The list in the findings rail is complete; only the document view is missing."}
        </div>
      )}

      {/*
        BELGE ANA YÜZEY (plan B3).

        `flex: 1` ve `minHeight: 0`: yükseklik artık sabit 620 px değil, kapsayıcıda ne
        varsa o. Kullanıcının sorusu "ne değişmiş" değil NEREDE değişmiş ve onun cevabı
        sayfada; sayfa da ekranın alabildiği kadarını almalı.
      */}
      {/*
        BELGE TEK YÜZEY.

        Bulgu/madde/taşıma/imza listeleri KALDIRILDI. Ürünün cevabı "ne değişmiş" listesi
        değil, "NEREDE değişmiş" işareti: iki belge yan yana akıyor ve değişiklikler sayfa
        üzerinde işaretli. Liste, o cevabın önüne geçen ve yüksekliği yiyen bir katmandı.

        Bulgunun ayrıntısı kaybolmadı: her işaretin üzerine gelindiğinde ne değiştiği
        (eski → yeni metin, önem ve güven) yazıyor — `collectHighlights` her dikdörtgene o
        başlığı koyuyor.
      */}
      <div style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>
        {viewerAvailable && (
          <div ref={panesRef} style={{ flex: 1, display: "flex", minWidth: 0, minHeight: 0, background: T.surface }}>
            <DocumentPane
              document={firstDocument ?? null}
              pageCount={result.first.pageCount}
              highlightsFor={(page: number): HighlightRect[] => collectHighlights(result, null, "first", page)}
              label={isTr ? "Birinci" : "First"}
              pageWidth={effectivePageWidth}
              registerApi={(api: DocumentPaneApi | null): void => {
                firstApi.current = api;
              }}
              onScrollPosition={
                syncActive ? (position: DocumentPosition): void => driveFrom("first", position) : undefined
              }
              isTr={isTr}
            />
            <div style={{ width: 1, background: T.border }} />
            <DocumentPane
              document={secondDocument ?? null}
              pageCount={result.second.pageCount}
              highlightsFor={(page: number): HighlightRect[] => collectHighlights(result, null, "second", page)}
              label={isTr ? "İkinci" : "Second"}
              pageWidth={effectivePageWidth}
              registerApi={(api: DocumentPaneApi | null): void => {
                secondApi.current = api;
              }}
              onScrollPosition={
                syncActive ? (position: DocumentPosition): void => driveFrom("second", position) : undefined
              }
              isTr={isTr}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Tek satırlık komut çubuğu: ölçüm · görüntüleme · raf.
 *
 * <p>Bütün boşluk, yükseklik ve yarıçap değerleri yukarıdaki ölçü sisteminden geliyor.
 * Yerinde sayı yazmak, bu çubuğun "derlenmiş" görünmesinin sebebiydi.</p>
 */
function renderCommandBar(
  result: CompareResultV2,
  pageWidth: number | null,
  setPageWidth: (width: number | null) => void,
  anchorMap: AnchorMap,
  syncEnabled: boolean,
  setSyncEnabled: (enabled: boolean) => void,
  isTr: boolean
): ReactNode {
  const s: CompareSummaryV2 = result.summary;

  return (
    <div style={barStyle}>
      {/*
        ANA ÖLÇÜM. Sayı ve birim AYNI taban çizgisinde; birim ve etiket aynı boyutta.
        Önceki sürümde `%` sayının içinde farklı bir boyutla duruyordu ve etiket üçüncü bir
        boyuttaydı — tek satırda üç boyut, gözün tutunacağı bir hiyerarşi bırakmıyor.
      */}
      <div style={{ ...groupStyle, gap: 7 }}>
        {/*
          `%` mono yazı tipinde 19 px'te alt simge gibi düşüyordu. Birim, sayının içinde
          değil etiketle birlikte ve gövde boyutunda: tek satırda iki boyut kalıyor.
        */}
        <span
          style={{
            ...TABULAR,
            fontFamily: MONO,
            fontSize: FONT_METRIC,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1
          }}
        >
          {s.similarityPercent.toFixed(1)}
        </span>
        <span style={{ color: T.dim }}>% {isTr ? "benzerlik" : "similar"}</span>
      </div>

      {divider()}

      {/*
        Sayım ve güven rozetleri AYNI grupta. Ayrı gruplarda dururken aralarındaki 16 px
        boşluk onları birbirinden kopuk iki küme gibi gösteriyordu; ikisi de aynı sorunun
        (ne değişti, ne kadar eminiz) cevabı ve birlikte okunuyor. Aradaki ince ayırıcı
        farkı boşluk büyütmeden veriyor.
      */}
      <div style={groupStyle}>
        {metricChip(`+${s.added}`, T.added, T.addedBg)}
        {metricChip(`−${s.removed}`, T.removed, T.removedBg)}
        {metricChip(`~${s.changed}`, T.changed, T.changedBg)}
        {s.moved > 0 && metricChip(`⇄${s.moved}`, T.moved, T.movedBg)}

        {(s.certain > 0 || s.suspect > 0 || s.weak > 0) && (
          <>
            {divider()}
            {s.certain > 0 &&
              metricChip(`${TIER_STYLES.certain[isTr ? "labelTr" : "labelEn"]} ${s.certain}`, T.removed, T.removedBg)}
            {s.suspect > 0 &&
              metricChip(`${TIER_STYLES.suspect[isTr ? "labelTr" : "labelEn"]} ${s.suspect}`, T.changed, T.changedBg)}
            {s.weak > 0 && metricChip(`${TIER_STYLES.weak[isTr ? "labelTr" : "labelEn"]} ${s.weak}`, T.dim, T.subtle)}
          </>
        )}
      </div>

      {/*
        Bağlam bilgisi: bastırılan bulgu sayısı ve referans taraf. İkisi de aynı yükseklik
        ve yarıçapta, serbest gezen metin değil — serbest metin, çubuğun rastgele
        görünmesinin ikinci sebebiydi.

        Bastırılan sayı GÖRÜNÜR olmak zorunda: sıfırdan büyükse tam liste sunucuda DURUYOR,
        sessizce silinmedi (değişmez I2). Eski motorda gürültü filtresinin silme yetkisi
        vardı ve eklenen bir ödeme vadesi maddesini silip "%100 aynı" demişti.
      */}
      {/*
        PASİF BİLGİ KENARLIK TAŞIMIYOR.

        İkisi de kenarlıklı ve zeminli kutulardı, yani komşu düğmelerle aynı görünüyordu —
        tıklanabilir sanılıp tıklanan, hiçbir şey yapmayan iki kontrol. Bir alette pasif
        bilgi düğme gibi çizilmez; aynı yükseklikte hizada kalıyor ama kutu yok.
      */}
      {/*
        İki pasif olgu arasında GRUP-ARASI boşluk: kutuları kalkınca 6 px'lik grup-içi
        boşlukla yan yana duruyorlar ve "3 gizli referans birinci belge" tek bir cümle gibi
        okunuyordu. Ayrı olgular ayrı nefes alır.
      */}
      <div style={{ ...groupStyle, gap: GAP_LOOSE }}>
        {s.suppressed > 0 && (
          <span
            style={{ ...passiveInfo(), color: T.dim }}
            title={
              isTr
                ? "Dekoratif glif, sayfa mobilyası ve kaşe metni olarak etiketlenip listeden çıkarıldı; sunucuda duruyor."
                : "Labelled as decoration, page furniture or stamp text and kept out of the list; still on the server."
            }
          >
            {isTr ? `${s.suppressed} gizli` : `${s.suppressed} hidden`}
          </span>
        )}
        <span
          style={{ ...passiveInfo(), color: T.dim }}
          title={`${result.reference.explanation}\n\n${describeSide(result, "first", isTr)}\n${describeSide(result, "second", isTr)}`}
        >
          {isTr ? "referans" : "reference"}
          <span style={{ color: T.text }}>{describeReferenceSide(result, isTr)}</span>
        </span>
      </div>

      {/* Esnek boşluk: sağdaki grup her zaman sağa yapışık. */}
      <span style={{ flex: 1, minWidth: GAP_TIGHT }} />

      {renderViewerToolbar(pageWidth, setPageWidth, anchorMap, syncEnabled, setSyncEnabled, isTr)}
    </div>
  );
}

/**
 * Ölçüm rozeti.
 *
 * Zemin de renk de token, yükseklik de ölçü sisteminden: aynı rozet açık ve koyu temada
 * okunabilir kalıyor ve komşu kontrollerle aynı hizada duruyor. Önceki sürüm hem yalnızca
 * koyu zemin için ayarlanmıştı hem de komşularından 6 px kısaydı.
 */
function metricChip(text: string, color: string, background: string): ReactNode {
  return (
    <span
      style={{
        ...controlBase(),
        ...TABULAR,
        fontFamily: MONO,
        padding: "0 7px",
        cursor: "default",
        background,
        color
      }}
    >
      {text}
    </span>
  );
}

/**
 * Görüntüleme kontrolleri: birleşik zoom segmenti + eşzamanlı kaydırma anahtarı.
 *
 * <p><b>Kapatılan tasarım hatası.</b> Üç ayrı düğme, ortada mono bir "420px · sığdır"
 * okuması ve çıplak bir native <c>checkbox</c> vardı. Serbest gezen okuma satırı hata
 * ayıklama çıktısı gibi duruyor, işletim sisteminin çizdiği checkbox ise etrafındaki
 * tasarımın hiçbir kuralına uymuyordu — o ikisi "profesyonel bir alet değil" izleniminin
 * büyük kısmıydı.</p>
 *
 * <p>Zoom artık tek bir segment: <c>− | değer | +</c>, paylaşılan kenarlıklarla. Değer
 * segmentin içinde ve tıklanınca sığdırmaya dönüyor, yani ayrı bir "Sığdır" düğmesi de
 * gerekmiyor. Eşzamanlı kaydırma ise komşularıyla aynı yükseklik ve yarıçapta gerçek bir
 * aç/kapa düğmesi.</p>
 *
 * Değerler CSS pixel cinsinden sayfa genişliği — <c>PdfPageCanvas</c> ölçeği kendi
 * hesaplıyor, yani burada hiçbir pixel dönüşümü tekrarlanmıyor.
 */
function renderViewerToolbar(
  pageWidth: number | null,
  setPageWidth: (width: number | null) => void,
  anchorMap: AnchorMap,
  syncEnabled: boolean,
  setSyncEnabled: (enabled: boolean) => void,
  isTr: boolean
): ReactNode {
  /**
   * Zoom adımı. Sığdırmadan çıkarken bir başlangıç genişliği gerekiyor: sığdırma değeri
   * panelin kendi kutusundan geliyor ve burada bilinmiyor, o yüzden makul bir taban
   * kullanılıyor. Kullanıcı iki tıkta istediği ölçeğe gidiyor.
   */
  const step = (delta: number): void => {
    const base: number = pageWidth ?? DEFAULT_ZOOM_WIDTH;
    const next: number = Math.round(base * (delta > 0 ? 1.25 : 0.8));
    setPageWidth(Math.max(MIN_PAGE_WIDTH, Math.min(MAX_PAGE_WIDTH, next)));
  };
  const fitting: boolean = pageWidth === null;

  /** Segmentin bir parçası: dış kenarlıklar segmentin, iç kenarlıklar paylaşılıyor. */
  const segment = (
    label: ReactNode,
    title: string,
    onClick: () => void,
    position: "left" | "middle" | "right",
    extra?: CSSProperties
  ): ReactNode => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        ...controlBase(),
        justifyContent: "center",
        minWidth: position === "middle" ? 74 : 26,
        padding: 0,
        borderRadius: 0,
        borderTopLeftRadius: position === "left" ? RADIUS : 0,
        borderBottomLeftRadius: position === "left" ? RADIUS : 0,
        borderTopRightRadius: position === "right" ? RADIUS : 0,
        borderBottomRightRadius: position === "right" ? RADIUS : 0,
        // Komşu kenarlıklar üst üste binmesin: sağdaki parça soldakinin kenarını paylaşıyor.
        marginLeft: position === "left" ? 0 : -1,
        ...extra
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={groupStyle}>
      <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        {segment("−", isTr ? "Uzaklaştır" : "Zoom out", (): void => step(-1), "left")}
        {segment(
          <span style={{ ...TABULAR, fontFamily: MONO, color: fitting ? T.dim : T.text }}>
            {fitting ? (isTr ? "sığdır" : "fit") : pageWidth}
          </span>,
          isTr ? "Genişliğe sığdır" : "Fit to width",
          (): void => setPageWidth(null),
          "middle"
        )}
        {segment("+", isTr ? "Yakınlaştır" : "Zoom in", (): void => step(1), "right")}
      </div>

      {/*
        EŞZAMANLI KAYDIRMA (plan B4).

        Çapa haritası yoksa kontrol HİÇ ÇİZİLMİYOR, yerine sebebi yazan bir işaret geliyor.
        Plan bunu açıkça söylüyor: "Çapa yoksa senkron kapanır ve kullanıcıya söylenir —
        sessizce yanlış hizalamak daha kötü." Oranlı bir eşlemeye düşmek tam olarak o sessiz
        yanlış hizalama olurdu ve sayfa sayıları farklı olduğunda (alarko: sol 1, sağ 2)
        çöker.
      */}
      {anchorMap.usable ? (
        <button
          type="button"
          onClick={(): void => setSyncEnabled(!syncEnabled)}
          aria-pressed={syncEnabled}
          title={isTr ? `${anchorMap.count} çapa üzerinden hizalanıyor` : `Aligned through ${anchorMap.count} anchors`}
          style={{
            ...controlBase(),
            borderColor: syncEnabled ? T.accent : "var(--kuika-cmp-border)",
            background: syncEnabled ? T.accent : "var(--kuika-cmp-subtle)",
            color: syncEnabled ? T.accentText : T.dim
          }}
        >
          {isTr ? "Eşzamanlı" : "Sync"}
        </button>
      ) : (
        <span
          style={{ ...controlBase(), cursor: "help", color: T.warn, background: T.warnBg }}
          title={
            isTr
              ? "İki taraf arasında ortak bir mantıksal düğüm bulunamadı. Oranlı bir eşlemeye düşmek sessizce yanlış hizalamak olurdu."
              : "No shared logical node between the two sides. Falling back to proportional mapping would be a silent misalignment."
          }
        >
          {isTr ? "çapa yok" : "no anchors"}
        </span>
      )}
    </div>
  );
}

/**
 * Çalışamayan yetenekler. **Bu banner sessizliğin panzehiri**: eski motorda dış araç
 * eksikliği (ImageMagick yok, render alınamadı) sessizce `null` dönüyordu ve kullanıcı
 * hiçbir şey olmamış gibi bir sonuç görüyordu. Sonuç EKSİKSE bu söylenmek zorunda.
 *
 * Koyu kroma taşındı: komut çubuğunun hemen altında, uyarı rengini koruyarak. Renk
 * anlamdır — dekor olmadığı için tema değişse de sarı kalıyor.
 */
function renderDegradedBanner(result: CompareResultV2, isTr: boolean): ReactNode {
  if (result.degradedFeatures.length === 0) return null;
  return (
    <div
      style={{
        borderBottom: `1px solid ${T.border}`,
        background: T.warnBg,
        padding: "8px 12px",
        color: T.warn,
        fontSize: 12
      }}
    >
      <strong>{isTr ? "Bu sonuç eksik" : "This result is incomplete"}</strong>
      <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
        {result.degradedFeatures.map((feature: string) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
    </div>
  );
}

function describeReferenceSide(result: CompareResultV2, isTr: boolean): string {
  if (result.reference.side === "first") return isTr ? "birinci belge" : "first document";
  if (result.reference.side === "second") return isTr ? "ikinci belge" : "second document";
  return isTr ? "hiçbiri (iki taraf eşit güvenilir)" : "neither (both sides equally reliable)";
}

function describeSide(result: CompareResultV2, which: "first" | "second", isTr: boolean): string {
  const side: CompareSideV2 = which === "first" ? result.first : result.second;
  const name: string = which === "first" ? (isTr ? "birinci" : "first") : isTr ? "ikinci" : "second";
  const pages: string = isTr ? "sayfa" : "pages";
  const words: string = isTr ? "kelime" : "words";
  return `${name}: ${side.extractor}, ${side.pageCount} ${pages}, ${side.wordCount} ${words}`;
}

/**
 * Sayfa görüntüleyicinin kullanılabilir olması iki tarafın PDF'inin de yüklenmiş olmasına
 * bağlı DEĞİL: tek taraf yüklenmişse o taraf gösterilir. Bulgu tek taraflı olabilir
 * (eklendi/silindi) ve o durumda karşı tarafta gösterilecek bir şey yok.
 */
function hasViewer(first?: PdfDocumentProxy | null, second?: PdfDocumentProxy | null): boolean {
  return !!first || !!second;
}

/** Bulgunun herhangi bir tarafında çizilebilir bir dikdörtgen var mı. */
// NOT: `renderPreview` / `renderPreviewSide` / `PREVIEW_WIDTH` KALDIRILDI (plan B3).
//
// Seçilen bulgunun iki tarafını 420 CSS px'lik tek bir sayfa olarak gösteriyorlardı.
// Belge artık ANA YÜZEY: `DocumentPane` iki tarafın bütün sayfalarını sürekli akış
// hâlinde çiziyor, zoom var ve o sayfadaki BÜTÜN işaretler görünüyor. Önizleme
// bileşenleri geride bırakılsa iki farklı görüntüleyici yolu olurdu ve eşiklerinin
// zamanla ayrışması v1'in en pahalı hata sınıfıydı.

/**
 * O sayfadaki tüm bulguların dikdörtgenleri; seçili olan belirgin, diğerleri soluk.
 * Renk ÖNEM eksenini taşıyor — güven ekseni ayrı ve listede çip olarak gösteriliyor.
 */
/**
 * Bir konumun ÇİZİLECEK dikdörtgenleri.
 *
 * Satır kutuları varsa onlar; yoksa sınırlayıcı kutuya düşülüyor. Geri düşüş şart çünkü
 * `rects` sözleşmeye sonradan eklendi ve eski bir sunucu onu göndermiyor — o durumda
 * vurgu kaybolmamalı, yalnızca eskisi kadar kaba olmalı.
 */
function rectsOf(locator: LocatorV2): number[][] {
  if (locator.rects && locator.rects.length > 0) return locator.rects;
  return locator.rect ? [locator.rect] : [];
}

/**
 * Bir sayfanın vurguları: o sayfadaki BÜTÜN bulgular + imza bölgeleri.
 *
 * `selected` null olabilir — belge ana yüzey olduğunda kullanıcı hiçbir bulguya
 * tıklamadan da sayfadaki her işareti görmek zorunda. Seçim yalnızca hangisinin
 * belirginleştirileceğini söylüyor, hangilerinin çizileceğini değil.
 */
function collectHighlights(
  result: CompareResultV2,
  selected: FindingV2 | null,
  which: "first" | "second",
  page: number
): HighlightRect[] {
  const highlights: HighlightRect[] = [];
  for (const candidate of result.findings) {
    const locator: LocatorV2 | null = which === "first" ? candidate.first : candidate.second;
    if (!locator || locator.page !== page) continue;

    // SATIR BAŞINA çiziliyor. Tek bir sınırlayıcı kutu, birden çok satıra yayılan bir
    // bulguda satırların arasındaki her şeyi de kapsıyordu: altur sf1'de bir görselin
    // içinden okunan dağınık token'lar sayfanın %48,9'unu kaplayan, hiçbir yeri işaret
    // etmeyen tek bir dikdörtgen üretiyordu. `rects` yoksa (eski sunucu) `rect`'e düşülüyor.
    const boxes: number[][] = rectsOf(locator);
    for (const box of boxes) {
      highlights.push({
        rect: box,
        color: SEVERITY_STYLES[candidate.severity].color,
        title: candidate.kind === "removed" ? candidate.firstText : candidate.secondText,
        selected: candidate === selected
      });
    }
  }

  // İmza bölgeleri de çiziliyor — ayrı renkte ve asla "seçili" değil. Bir bulguya
  // bakarken sayfadaki imzayı da görmek, "bu değişiklik imzanın içinde mi" sorusunu
  // gözle cevaplatıyor; motorun bastırma kararını kullanıcı denetleyebiliyor.
  for (const signature of result.signatures) {
    if (!signature.isSignature) continue;
    if (signature.side !== which) continue;
    if (!signature.locator || signature.locator.page !== page) continue;
    // İmza bölgesi tek bir alan: sınırlayıcı kutu burada DOĞRU çizim. Bölge zaten bir
    // dikdörtgen olarak tespit ediliyor, satırlara yayılmış bir metin değil.
    for (const box of rectsOf(signature.locator)) {
      highlights.push({
        rect: box,
        color: SIGNATURE_HIGHLIGHT_COLOR,
        title: signatureTitle(signature, true),
        selected: false
      });
    }
  }
  return highlights;
}

/**
 * İmza vurgusunun başlığı: tür · karar · güven.
 *
 * Liste kaldırıldıktan sonra imza bilgisinin kullanıcıya ulaştığı TEK yer bu başlık. Bir
 * dikdörtgenin üzerine gelindiğinde motorun o bölge hakkındaki kararı okunuyor — imza
 * ekseni sessizce ölmedi, listeden sayfaya taşındı (değişmez I6).
 */
function signatureTitle(signature: SignatureV2, isTr: boolean): string {
  const kind: BilingualLabel | undefined = SIGNATURE_KIND_LABELS[signature.kind];
  const verdict: BilingualLabel | undefined = SIGNATURE_VERDICT_LABELS[signature.verdict];
  const kindText: string = kind ? (isTr ? kind.tr : kind.en) : signature.kind;
  const verdictText: string = verdict ? (isTr ? verdict.tr : verdict.en) : signature.verdict;
  const percent: string = `${Math.round(signature.confidence * 100)}%`;
  return `${kindText} · ${verdictText} · ${percent}`;
}

/**
 * İmza vurgusunun rengi. Önem eksenindeki hiçbir renkle çakışmıyor: sayfada bir
 * dikdörtgen görüldüğünde onun bir İÇERİK bulgusu mu bir İMZA bölgesi mi olduğu renkten
 * anlaşılmalı, tıklamaya gerek kalmadan.
 */
const SIGNATURE_HIGHLIGHT_COLOR = "#7c3aed";

/** Bölgenin tek satırlık adı: "kaşe · imzalanırken eklenmiş · %95". */
